import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Info, MessagesSquare, Send } from 'lucide-react';
import {
  Button, EmptyState, EntityAvatar, IconButton, SkeletonText, Spinner, Textarea, Tooltip, cn, message,
} from '../../ui';
import { AttachmentViewer } from '../../components/AttachmentPreview';
import { fileLabel } from '../../lib/fileName';
import { formatIst, formatIstLong } from '../../lib/time';
import { plural } from '../../lib/format';
import {
  CHAT_BODY_MAX, THREAD_POLL_MS,
  chatApi, chatErr, chatKeys, conversationTitle, idGt,
  type ChatConversation, type ChatMessage,
} from './chatApi';
import { ChatInfoDrawer, ConversationAvatar, ConversationTypeTag, dayKey, dayLabel, isImageUrl } from './ChatParts';

const PAGE = 50;

/** Merge a batch into the thread, de-duplicated by id and kept ascending. */
function mergeMessages(cur: ChatMessage[], add: ChatMessage[]): ChatMessage[] {
  if (!add.length) return cur;
  const seen = new Set(cur.map((m) => m.id));
  const fresh = add.filter((m) => !seen.has(m.id));
  if (!fresh.length) return cur;
  return [...cur, ...fresh].sort((a, b) => (idGt(a.id, b.id) ? 1 : idGt(b.id, a.id) ? -1 : 0));
}

const isVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

/**
 * One conversation: header, the day-grouped message list and the composer.
 *
 * Mounted with `key={id}` by the page, so switching rooms starts from a clean slate. The thread is
 * held in local state rather than react-query because it GROWS from both ends — polled forward with
 * `after=<last id>` every 4 s while the tab is visible, paged backward with `before=<first id>` —
 * which a single cached query result does not model.
 *
 * TEXT ONLY: the composer has no attach button. Attachments already on a message (older ones, or
 * sent from another client) are still shown and open in the viewer.
 *
 * `compact` tightens the chrome for the floating chat panel. `onExit` is where "Back to chats" and
 * leaving a group go; without it they navigate to `/chat` (the full-screen page).
 */
export function ChatThread({
  id, summary, onBack, onExit, compact = false,
}: { id: string; summary?: ChatConversation; onBack?: () => void; onExit?: () => void; compact?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const exit = onExit ?? (() => navigate('/chat'));

  const detailQ = useQuery({
    queryKey: chatKeys.detail(id),
    queryFn: () => chatApi.detail(id),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: (n, e: any) => e?.response?.status !== 403 && e?.response?.status !== 404 && n < 2,
  });
  const detail = detailQ.data;
  const head: ChatConversation | undefined = detail ?? summary;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [viewer, setViewer] = useState<{ files: string[]; index: number } | null>(null);

  const lastIdRef = useRef<string | null>(null);
  const pollingRef = useRef(false);
  const markedRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const prependAnchor = useRef<{ height: number; top: number } | null>(null);

  const lastId = messages.length ? messages[messages.length - 1].id : null;
  useEffect(() => { lastIdRef.current = lastId; }, [lastId]);

  /* ── Initial page ── */
  useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);
    setLoadError(null);
    chatApi.messages(id, { limit: PAGE })
      .then((rows) => {
        if (cancelled) return;
        stickToBottom.current = true;
        setMessages(rows ?? []);
        setHasMore((rows ?? []).length >= PAGE);
      })
      .catch((e) => { if (!cancelled) setLoadError(chatErr(e, 'Could not load messages')); })
      .finally(() => { if (!cancelled) setInitialLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  /* ── Forward poll: after=<last id>, every 4 s while visible ── */
  const pollNew = useCallback(async () => {
    if (pollingRef.current || !isVisible()) return;
    const after = lastIdRef.current;
    pollingRef.current = true;
    try {
      const rows = await chatApi.messages(id, after ? { after, limit: 100 } : { limit: PAGE });
      if (rows?.length) {
        const el = scrollRef.current;
        stickToBottom.current = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        setMessages((cur) => mergeMessages(cur, rows));
      }
    } catch {
      /* a missed poll is retried in 4 s — not worth a toast */
    } finally {
      pollingRef.current = false;
    }
  }, [id]);

  useEffect(() => {
    if (initialLoading || loadError) return;
    const t = window.setInterval(() => { void pollNew(); }, THREAD_POLL_MS);
    const onVis = () => { if (isVisible()) void pollNew(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [initialLoading, loadError, pollNew]);

  /* ── Mark read: on open and whenever a newer message lands (only while the reader can see it) ── */
  const markRead = useCallback((upTo: string) => {
    if (markedRef.current && !idGt(upTo, markedRef.current)) return;
    markedRef.current = upTo;
    chatApi.markRead(id, upTo)
      .then(() => {
        // Clear this row's unread locally so the list does not wait for its next poll.
        qc.setQueryData<ChatConversation[]>(chatKeys.list, (rows) =>
          rows?.map((r) => (r.id === id ? { ...r, unread: 0 } : r)));
        qc.invalidateQueries({ queryKey: chatKeys.unread });
      })
      .catch(() => { markedRef.current = null; });
  }, [id, qc]);

  useEffect(() => {
    if (!lastId) return;
    if (isVisible()) { markRead(lastId); return; }
    const onVis = () => { if (isVisible() && lastIdRef.current) markRead(lastIdRef.current); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [lastId, markRead]);

  /* ── Older page: before=<first id>, keeping the reader's place ── */
  const loadOlder = async () => {
    if (!messages.length || loadingOlder) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    try {
      const rows = await chatApi.messages(id, { before: messages[0].id, limit: PAGE });
      if (el) prependAnchor.current = { height: el.scrollHeight, top: el.scrollTop };
      stickToBottom.current = false;
      setMessages((cur) => mergeMessages(cur, rows ?? []));
      setHasMore((rows ?? []).length >= PAGE);
    } catch (e) {
      message.error(chatErr(e, 'Could not load earlier messages'));
    } finally {
      setLoadingOlder(false);
    }
  };

  /* ── Scroll management ── */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prependAnchor.current) {
      const { height, top } = prependAnchor.current;
      el.scrollTop = el.scrollHeight - height + top;
      prependAnchor.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  /* ── Composer (text only) ── */
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setDraft(''); }, [id]);

  // Grow the box with its content, up to ~6 lines.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  // Sending is NOT behind a Yes/No: a chat that asks "Send this message?" on every Enter is not a
  // chat. Nothing here edits or removes an existing record.
  const send = useMutation({
    mutationFn: (text: string) => chatApi.send(id, { body: text }),
    onSuccess: (m) => {
      stickToBottom.current = true;
      setMessages((cur) => mergeMessages(cur, [m]));
      setDraft('');
      qc.invalidateQueries({ queryKey: chatKeys.list });
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    onError: (e) => message.error(chatErr(e, 'Message not sent')),
  });

  const body = draft.trim();
  const canSend = body.length > 0 && !send.isPending && body.length <= CHAT_BODY_MAX;
  const doSend = () => {
    if (!canSend) return;
    send.mutate(body);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      doSend();
    }
  };

  /* ── Render ── */
  const detailErr = detailQ.error as any;
  const errCode = detailErr?.response?.data?.error?.code;
  if (detailQ.isError && (errCode === 'CHAT_NOT_MEMBER' || detailErr?.response?.status === 404)) {
    return (
      <div className="flex h-full flex-col">
        {onBack && (
          <div className="border-b border-line p-2">
            <Button variant="text" icon={<ArrowLeft size={16} />} onClick={onBack}>Chats</Button>
          </div>
        )}
        <EmptyState
          className="flex-1"
          icon={<MessagesSquare />}
          title="You're not in this conversation"
          description="You may have left it, or been removed from the group."
          action={<Button onClick={exit}>Back to chats</Button>}
        />
      </div>
    );
  }

  const title = head ? conversationTitle(head) : '';
  const subtitle = !head
    ? ''
    : head.type === 'DIRECT'
      ? (head.otherUser?.roleLabel ?? '')
      : plural(detail?.members.length ?? head.memberCount, 'member');
  const showSenders = head ? head.type !== 'DIRECT' : true;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className={cn('flex items-center border-b border-line', compact ? 'gap-2 px-2 py-1.5' : 'gap-2.5 px-3 py-2.5 sm:px-4')}>
        {onBack && (
          <IconButton aria-label="Back to chats" diameter={compact ? 32 : 36} className="text-content/70 hover:bg-surface-2" onClick={onBack}>
            <ArrowLeft />
          </IconButton>
        )}
        {head
          ? <ConversationAvatar c={head} size={compact ? 32 : 38} />
          : <span className={cn('shrink-0 rounded-pill bg-surface-3', compact ? 'size-8' : 'size-[38px]')} />}
        <button type="button" onClick={() => setInfoOpen(true)} className="min-w-0 flex-1 text-left">
          <span className="flex items-center gap-2">
            <span className="truncate type-body-strong text-content">{title || '…'}</span>
            {head && <ConversationTypeTag type={head.type} />}
          </span>
          {subtitle && <span className="block truncate type-caption text-subtle">{subtitle}</span>}
        </button>
        <Tooltip label={head?.type === 'DIRECT' ? 'Chat info' : 'Group info'}>
          <IconButton aria-label="Conversation info" diameter={compact ? 32 : 36} className="text-content/70 hover:bg-surface-2" onClick={() => setInfoOpen(true)}>
            <Info />
          </IconButton>
        </Tooltip>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className={cn('min-h-0 flex-1 overflow-y-auto bg-surface-2 dark:bg-black/20', compact ? 'px-2.5 py-2' : 'px-3 py-3 sm:px-5')}>
        {initialLoading ? (
          <div className="space-y-4 p-2"><SkeletonText lines={6} /></div>
        ) : loadError ? (
          <EmptyState
            icon={<MessagesSquare />}
            title="Could not load this conversation"
            description={loadError}
            action={<Button onClick={() => navigate(0)}>Reload</Button>}
          />
        ) : messages.length === 0 ? (
          <EmptyState icon={<MessagesSquare />} title="No messages yet" description={`Say hello to ${title || 'the group'}.`} />
        ) : (
          <>
            {hasMore && (
              <div className="mb-3 flex justify-center">
                <Button size="sm" variant="default" loading={loadingOlder} onClick={loadOlder}>Load earlier messages</Button>
              </div>
            )}
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const newDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);
              const runStart = newDay || prev.senderId !== m.senderId;
              const showMeta = showSenders && !m.mine && runStart;
              return (
                <Fragment key={m.id}>
                  {newDay && (
                    <div className="my-3 flex justify-center">
                      <span className="rounded-pill border border-line bg-surface px-2.5 py-0.5 type-caption text-subtle">
                        {dayLabel(m.createdAt)}
                      </span>
                    </div>
                  )}
                  <div className={cn('flex gap-2', m.mine ? 'justify-end' : 'justify-start', runStart ? 'mt-2.5' : 'mt-0.5')}>
                    {showSenders && !m.mine && (
                      <span className="w-7 shrink-0">
                        {showMeta && <EntityAvatar name={m.senderName} src={m.senderPhotoUrl} size={28} />}
                      </span>
                    )}
                    <div className={cn('flex flex-col', compact ? 'max-w-[84%]' : 'max-w-[78%]', m.mine ? 'items-end' : 'items-start')}>
                      {showMeta && <span className="mb-0.5 px-1 type-caption-strong text-muted">{m.senderName}</span>}
                      <div
                        title={formatIstLong(m.createdAt)}
                        className={cn(
                          'rounded-2xl px-3 py-2',
                          m.mine
                            ? 'rounded-br-sm bg-brand-600 text-white'
                            : 'rounded-bl-sm border border-line bg-surface text-content',
                        )}
                      >
                        {m.attachments?.length > 0 && (
                          <div className={cn('grid gap-1', m.attachments.length > 1 ? 'grid-cols-2' : 'grid-cols-1', m.body && 'mb-1.5')}>
                            {m.attachments.map((u, ai) => (
                              isImageUrl(u) ? (
                                <button
                                  key={`${u}-${ai}`}
                                  type="button"
                                  onClick={() => setViewer({ files: m.attachments, index: ai })}
                                  className="block overflow-hidden rounded-control"
                                  aria-label={`Open photo ${ai + 1}`}
                                >
                                  <img
                                    src={u}
                                    alt=""
                                    loading="lazy"
                                    className={cn(
                                      'object-cover',
                                      m.attachments.length > 1
                                        ? (compact ? 'size-24' : 'size-28 sm:size-32')
                                        : (compact ? 'max-h-48 w-full max-w-[13rem]' : 'max-h-64 w-full max-w-[16rem]'),
                                    )}
                                  />
                                </button>
                              ) : (
                                <a key={`${u}-${ai}`} href={u} target="_blank" rel="noopener noreferrer"
                                  className={cn('truncate type-caption underline', m.mine ? 'text-white!' : 'text-brand-700!')}>
                                  {fileLabel(u, { index: ai + 1 })}
                                </a>
                              )
                            ))}
                          </div>
                        )}
                        {m.body && <div className="whitespace-pre-wrap break-words type-body">{m.body}</div>}
                      </div>
                      <span className="mt-0.5 px-1 text-2xs text-subtle">{formatIst(m.createdAt, 'hh:mm A')}</span>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </>
        )}
      </div>

      {/* Composer — text only */}
      <div className={cn('border-t border-line bg-surface', compact ? 'p-2' : 'p-2.5 sm:p-3')}>
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Textarea
              ref={inputRef}
              rows={1}
              value={draft}
              maxLength={CHAT_BODY_MAX}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={compact ? 'Write a message…' : 'Write a message…  (Enter to send, Shift+Enter for a new line)'}
              aria-label="Message"
              className={cn('min-h-10 resize-none py-2', compact ? 'max-h-28' : 'max-h-40')}
            />
            {draft.length > CHAT_BODY_MAX - 200 && (
              <div className="mt-0.5 text-right text-2xs text-subtle">{draft.length}/{CHAT_BODY_MAX}</div>
            )}
          </div>
          <Tooltip label="Send">
            <IconButton
              aria-label="Send"
              diameter={40}
              disabled={!canSend}
              onClick={doSend}
              className="bg-brand-600 text-white hover:bg-brand-700 disabled:bg-surface-3 disabled:text-subtle"
            >
              {send.isPending ? <Spinner /> : <Send />}
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <ChatInfoDrawer open={infoOpen} onClose={() => setInfoOpen(false)} detail={detail} onLeft={onExit} />
      {viewer && (
        <AttachmentViewer
          files={viewer.files}
          index={viewer.index}
          label="Photo"
          onIndex={(index) => setViewer((v) => (v ? { ...v, index } : v))}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
